import { useMutation } from '@apollo/client/react';
import { FormikInput } from '../../../components/formik/FormInput';
import { FormikSelect } from '../../../components/formik/FormSelect';
import { Option } from '../../../components/inputs/Select';
import { CreateModal } from '../../../components/Modal/CreateModal';
import { UserContext } from '../../../contexts/UserContext';
import { PROJECTS, PROJECT_CLONE_FROM_CLOUD } from '../../../graphql/project';
import { T, useT } from '../../../translation/src';
import { Project, ProjectCloneFromCloudMutation, ProjectCloneFromCloudMutationVariables } from '../../../types/graphqlTypes';
import { FormikConfig } from 'formik';
import { memo, useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useValidationSchema } from '../ProjectModal';

interface FormValues {
  name: string;
  targetOrganisationId: string;
}

const useTranslations = () => {
  const namePlaceholder = useT('enter a project name', { swc: true });
  return { namePlaceholder };
};

interface ProjectCloneFromCloudModalProps {
  onClose: () => void;
  open?: boolean;
  project: Pick<Project, 'id' | 'name'>;
}

const ProjectCloneFromCloudModal_: React.FC2<ProjectCloneFromCloudModalProps> = ({ onClose, open, project }) => {
  const validationSchema = useValidationSchema();
  const translations = useTranslations();
  const { organisationId = '' } = useParams();
  const currentUser = useContext(UserContext);
  const [cloneFromCloud, { loading: isSubmitting }] = useMutation<ProjectCloneFromCloudMutation, ProjectCloneFromCloudMutationVariables>(PROJECT_CLONE_FROM_CLOUD);

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    (values) => {
      cloneFromCloud({
        variables: { projectId: project.id, name: values.name, targetOrganisationId: values.targetOrganisationId },
        onCompleted: onClose,
        refetchQueries: [{ query: PROJECTS, variables: { organisationId: values.targetOrganisationId } }],
        awaitRefetchQueries: true,
      });
    },
    [cloneFromCloud, onClose, project.id],
  );

  const formik: FormikConfig<FormValues> = useMemo(
    () => ({
      initialValues: { name: project.name, targetOrganisationId: organisationId },
      onSubmit,
      validationSchema,
    }),
    [onSubmit, validationSchema, project.name, organisationId],
  );

  return (
    <CreateModal title={<T _str="clone '{name}' from cloud" args={{ name: project.name }} swc />} createButtonTitle={<T _str="clone from cloud" swc />} formik={formik} onClose={onClose} open={open} isSubmitting={isSubmitting} noOverflow>
      <div className="grid grid-cols-1 gap-6 mb-6">
        <FormikInput autoFocus name="name" label={<T _str="name" swc />} placeholder={translations.namePlaceholder} />
        <FormikSelect name="targetOrganisationId" label={<T _str="target organisation" swc />}>
          {currentUser.organisations.map((org) => (
            <Option key={org.id} value={org.id}>{org.name}</Option>
          ))}
        </FormikSelect>
      </div>
    </CreateModal>
  );
};

export const ProjectCloneFromCloudModal = memo(ProjectCloneFromCloudModal_);
